import React, { useEffect, useRef, useState } from 'react';
import { WEB_MODAL_MAX_WIDTH } from '../../theme/webLayoutWidths';
import {
  StyleSheet,
  View,
  Keyboard,
  Platform,
  useWindowDimensions,
} from 'react-native';
import type { KeyboardEvent } from 'react-native';
import { Snackbar, Text, Portal, IconButton, Button } from 'react-native-paper';
import {
  AppSnackbar,
  type AppSnackbarPayload,
} from '../../services/AppSnackbar';
import { fs, iconSize, v, s } from '../../utils/libs';
import { DEFAULT_DURATION } from '../../CONSTANTS';
import { GTFonts, useTheme } from '../../theme';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';

const isWeb = Platform.OS === 'web';

const getBackgroundColor = (type: AppSnackbarPayload['type'], colors: any) => {
  switch (type) {
    case 'success':
      return colors.successDark;
    case 'error':
      return colors.errorDark;
    case 'warning':
      return colors.warningDark;
    case 'info':
    default:
      return colors.info;
  }
};

const AppSnackbarHost: React.FC = () => {
  const { theme } = useTheme();
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);
  const [visible, setVisible] = useState(false);
  const [payload, setPayload] = useState<AppSnackbarPayload | null>(null);
  const dismissTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    const cleanup = AppSnackbar.registerHost(next => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }

      if (next) {
        setPayload(next);
        setVisible(true);
        const duration = next.durationMs ?? DEFAULT_DURATION;
        dismissTimerRef.current = setTimeout(() => setVisible(false), duration);
      } else {
        setVisible(false);
      }
    });
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      cleanup();
    };
  }, []);

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const onShow = (e: KeyboardEvent) => {
      const height = e?.endCoordinates?.height ?? 0;
      setKeyboardInset(height);
    };

    const onHide = () => {
      setKeyboardInset(0);
    };

    const subShow = Keyboard.addListener(showEvent, onShow);
    const subHide = Keyboard.addListener(hideEvent, onHide);

    return () => {
      subShow.remove();
      subHide.remove();
    };
  }, []);

  const backgroundColor = getBackgroundColor(payload?.type, theme.colors);
  const handleDismiss = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setVisible(false);
  };

  return (
    <View style={styles.host}>
      <Portal>
        {visible && (
          <View
            style={styles.tapBarrier}
            onStartShouldSetResponder={() => true}
          />
        )}
        <Snackbar
          visible={visible}
          onDismiss={handleDismiss}
          duration={payload?.durationMs ?? DEFAULT_DURATION}
          style={[
            styles.snackbar,
            constrainWidth ? styles.snackbarConstrained : null,
            { backgroundColor },
            { marginBottom: v(8) + keyboardInset },
          ]}>
          <View
            style={styles.contentRow}
            pointerEvents={visible ? 'auto' : 'none'}
            onStartShouldSetResponder={() => visible}>
            <Text
              style={[
                styles.text,
                styles.message,
                { color: theme.colors.textInverse },
              ]}>
              {payload?.message ?? ''}
            </Text>
            <View
              style={styles.actionsContainer}
              pointerEvents={visible ? 'auto' : 'none'}
              onStartShouldSetResponder={() => visible}>
              {payload?.actionLabel ? (
                <Button
                  mode="text"
                  compact
                  hitSlop={v(50)}
                  onPress={() => payload?.onAction?.()}
                  textColor={theme.colors.textInverse}
                  style={styles.actionButton}
                  labelStyle={styles.actionLabel}>
                  {payload.actionLabel}
                </Button>
              ) : null}
              <IconButton
                icon="close"
                size={iconSize(18)}
                onPress={handleDismiss}
                hitSlop={v(50)}
                style={styles.closeButton}
                iconColor={theme.colors.textInverse}
                accessibilityLabel="Dismiss"
              />
            </View>
          </View>
        </Snackbar>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  tapBarrier: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: v(100),
  },
  snackbar: {
    marginHorizontal: s(8),
    marginBottom: v(8),
    borderRadius: s(8),
  },
  snackbarConstrained: {
    maxWidth: WEB_MODAL_MAX_WIDTH,
    alignSelf: 'center',
    width: '100%',
  },
  contentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  message: {
    flex: 1,
    marginRight: s(8),
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    marginRight: s(4),
  },
  actionLabel: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(13),
  },
  closeButton: {
    margin: 0,
  },
});

export default AppSnackbarHost;
